import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

import { installAxiosRefresh, loginSession, logoutSession } from '../services/authSession';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [sessionWarning, setSessionWarning] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const warn = () => setSessionWarning('Sessionen kunne ikke fornyes. Prøv din handling igen.');
    const synchronize = () => {
      setSessionWarning('');
      const token = localStorage.getItem('accessToken');
      let storedUser = null;
      try { storedUser = JSON.parse(localStorage.getItem('user')); } catch (_error) { /* Invalid persisted state is signed out. */ }
      setAccessToken(token);
      setUser(token ? storedUser : null);
      if (token) axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      else delete axios.defaults.headers.common.Authorization;
    };
    const synchronizeStorage = event => {
      if (event.storageArea === localStorage &&
          (event.key === null || ['accessToken', 'user', 'isAuthenticated'].includes(event.key))) {
        synchronize();
      }
    };
    synchronize();
    setLoading(false);
    window.addEventListener('storage', synchronizeStorage);
    window.addEventListener('auth-session-changed', synchronize);
    window.addEventListener('auth-session-retryable', warn);
    const removeInterceptor = installAxiosRefresh();
    return () => {
      window.removeEventListener('storage', synchronizeStorage);
      window.removeEventListener('auth-session-changed', synchronize);
      window.removeEventListener('auth-session-retryable', warn);
      removeInterceptor();
    };
  }, []);

  const login = async (username, password) => {
    try {
      const response = await loginSession(username, password);

      if (response.data.success) {
        return { success: true };
      } else {
        return { success: false, error: 'Forkert brugernavn eller password' };
      }
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Forkert brugernavn eller password'
      };
    }
  };

  const logout = async () => {
    try { await logoutSession(); return { success: true }; }
    catch (_error) { return { success: false, error: 'Du kunne ikke logges ud. Prøv igen.' }; }
  };

  const value = {
    user,
    accessToken,
    login,
    logout,
    isAuthenticated: !!user && !!accessToken,
    loading,
    sessionWarning
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
